/**
 * gm-variant-instant.js
 *
 * Repaints the price / SKU / Add-to-Cart button the instant a variant option is clicked on the
 * custom-product PDP, instead of waiting for product-info.js's `?section_id=` round trip (which
 * re-renders the whole 3,000-line main-product section server-side and takes ~0.5-2s).
 *
 * The values come from the pre-rendered blob emitted by snippets/gm-variant-data.liquid, whose
 * fragments are byte-identical to what that background fetch returns — so when the fetch does
 * land and product-info.js overwrites the same nodes, nothing visibly changes.
 *
 * This does NOT replace the fetch. The fetch still runs and remains the source of truth for
 * everything the client cannot know: which option combinations are still purchasable, quantity
 * rules, pickup availability, dynamic checkout.
 */
if (!customElements.get('gm-variant-instant')) {
  customElements.define(
    'gm-variant-instant',
    class GmVariantInstant extends HTMLElement {
      unsubscriber = undefined;
      index = null;

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.sectionId = this.productInfo.dataset.section;
        this.index = this.buildIndex();
        // No blob (single-variant product, or >100 variants) — stay dormant and let the stock
        // fetch-only behaviour apply unchanged.
        if (!this.index) return;

        this.unsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
      }

      disconnectedCallback() {
        this.unsubscriber?.();
      }

      /**
       * Keys are built here, in JS, from the option strings Liquid emitted — rather than having
       * Liquid emit a pre-joined key string. A delimiter-joined key would silently mis-map any
       * option value containing the delimiter, and Liquid's `| json` escaping can differ from
       * JSON.stringify's. Building both sides of the key with the same serializer avoids both.
       */
      buildIndex() {
        const blob = document.getElementById(`VariantData-${this.sectionId}`);
        if (!blob) return null;

        let variants;
        try {
          variants = JSON.parse(blob.textContent);
        } catch (e) {
          console.error('gm-variant-instant: could not parse variant data', e);
          return null;
        }
        if (!Array.isArray(variants) || !variants.length) return null;

        const index = new Map();
        variants.forEach((variant) => index.set(GmVariantInstant.key([variant.o1, variant.o2, variant.o3]), variant));
        return index;
      }

      /** Unused option slots arrive as null; drop them so the key length matches the live picker. */
      static key(options) {
        return JSON.stringify(options.filter((option) => option !== null && option !== undefined));
      }

      handleOptionValueChange({ data: { event, target } }) {
        if (!this.productInfo.contains(event.target)) return;

        // Product-swap path (linked products): the click navigates to a different product, which
        // this blob knows nothing about. Leave it entirely to product-info.js.
        const productUrl = target.dataset.productUrl;
        if (productUrl && productUrl !== this.productInfo.dataset.url) return;

        const variant = this.index.get(GmVariantInstant.key(this.selectedOptions));
        // Combination maps to no variant — genuinely needs the server to resolve. Painting a
        // guess here would be worse than the wait.
        if (!variant) return;

        // Painted in a microtask, NOT inline. product-info.js's own subscriber calls
        // resetProductFormState() -> toggleSubmitButton(true), which disables the ATC button on
        // every click; pubsub.js runs subscribers in registration order, and that order depends
        // on custom-element upgrade order. A microtask always runs after every synchronous
        // subscriber, yet still before the browser paints — so this is both order-independent
        // and visually instant.
        queueMicrotask(() => this.render(variant));
      }

      /** Live selection, read straight off the checked radios (DOM-decoded values). */
      get selectedOptions() {
        const picker = this.productInfo.querySelector('variant-selects');
        if (!picker) return [];
        return Array.from(picker.querySelectorAll('fieldset input:checked')).map((input) => input.value);
      }

      render(variant) {
        this.updatePrice(variant);
        this.updateSku(variant);
        this.updateSubmitButton(variant);
        this.updateVariantInputs(variant);
        this.updateURL(variant);
        this.updateMedia(variant);
      }

      updatePrice(variant) {
        const price = document.getElementById(`price-${this.sectionId}`);
        if (price) price.innerHTML = variant.priceHtml;
      }

      updateSku(variant) {
        // The #Sku-* element only exists at all when the *initially* selected variant had a SKU
        // (see the `sku` block in main-product.liquid), so a missing node is expected, not an error.
        const sku = document.getElementById(`Sku-${this.sectionId}`);
        if (!sku) return;
        sku.innerHTML = variant.skuHtml;
        sku.classList.toggle('hidden', variant.skuHtml === '');
      }

      updateSubmitButton(variant) {
        const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!button) return;
        button.toggleAttribute('disabled', variant.btnDisabled);
        const text = button.querySelector('span');
        // textContent, never innerHTML — product-form.js caches this node in its constructor.
        if (text) text.textContent = variant.btnText;
      }

      updateVariantInputs(variant) {
        this.productInfo
          .querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`)
          .forEach((form) => {
            const input = form.querySelector('input[name="id"]');
            if (!input) return;
            input.value = variant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateURL(variant) {
        this.productInfo.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${this.productInfo.dataset.url}?variant=${variant.id}`
        );
        if (this.productInfo.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.productInfo.dataset.url}?variant=${variant.id}`);
      }

      updateMedia(variant) {
        if (!variant.mediaId) return;
        this.productInfo.querySelector('media-gallery')?.setActiveMedia?.(`${this.sectionId}-${variant.mediaId}`, true);
      }
    }
  );
}
